import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { DialogModule } from 'primeng/dialog'
import { ButtonModule } from 'primeng/button'
import { SelectModule } from 'primeng/select'
import { DatePickerModule } from 'primeng/datepicker'
import { InputSwitchModule } from 'primeng/inputswitch'
import { MultiSelectModule } from 'primeng/multiselect'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { ShiftOriginModel } from '../../../../core/models/shift-origin.model'
import { UserModel } from '../../../../core/models/user.model'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { DateTools } from '../../../../core/tools/date-tools'
import { combineLatest, of } from 'rxjs'
import { Textarea } from 'primeng/textarea'

@Component({
  selector: 'app-shift-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    SelectModule,
    DatePickerModule,
    ReactiveFormsModule,
    InputSwitchModule,
    ProgressSpinnerModule,
    MultiSelectModule,
    Textarea,
  ],
  templateUrl: './shift-modal.component.html',
  styleUrls: ['./shift-modal.component.scss'],
})
export class ShiftModalComponent implements OnChanges {
  @Input() visible: boolean = false
  @Input() selectedEvent: any = null
  @Input() shiftOrigins: ShiftOriginModel[] = []
  @Input() loadingShiftOrigins: boolean = false
  @Input() submitting: boolean = false
  @Input() availableTechnicians: UserModel[] = []
  @Input() allTechnicians: UserModel[] = []

  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() onSubmit = new EventEmitter<any>()
  @Output() onCancel = new EventEmitter<void>()

  shiftForm: FormGroup

  // Propriété pour suivre si le formulaire a déjà été initialisé pour ce cycle d'ouverture
  private formInitialized = false

  // Propriété pour suivre le créneau horaire sélectionné
  selectedTimeSlot: 'morning' | 'afternoon' | 'custom' = 'custom'

  // Variable pour suivre si le créneau est cohérent avec la date
  timeSlotMismatch: boolean = false

  constructor(private fb: FormBuilder) {
    this.shiftForm = this.createForm()

    // Observer les changements sur startDate et les créneaux horaires
    combineLatest([
      this.shiftForm.get('startDate')?.valueChanges || of(null),
      this.shiftForm.get('startTimeSlot')?.valueChanges || of(null),
      this.shiftForm.get('endTimeSlot')?.valueChanges || of(null),
      this.shiftForm.get('isTimeSlot')?.valueChanges || of(false),
    ]).subscribe(([startDate, startTimeSlot, endTimeSlot, isTimeSlot]) => {
      // Vérifier la cohérence uniquement si un créneau est activé
      if (isTimeSlot && startDate && startTimeSlot && endTimeSlot) {
        this.checkTimeSlotConsistency()
      } else {
        this.timeSlotMismatch = false
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Initialiser le formulaire uniquement lorsque le modal devient visible
    // et que le formulaire n'a pas déjà été initialisé dans ce cycle
    if (
      changes['visible'] &&
      changes['visible'].currentValue === true &&
      !this.formInitialized
    ) {
      this.initializeForm()
      this.formInitialized = true
    }
    // Réinitialiser le flag lorsque le modal se ferme
    else if (changes['visible'] && changes['visible'].currentValue === false) {
      this.formInitialized = false
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      techniciansIds: [[], Validators.required],
      startDate: [null, Validators.required],
      duration: [null, Validators.required],
      shiftOriginId: [null, Validators.required],
      reason: [null],
      isTimeSlot: [false],
      startTimeSlot: [null],
      endTimeSlot: [null],
    })
  }

  private initializeForm(): void {
    this.shiftForm.reset()

    console.log(
      'Initializing shift form with selected event:',
      this.selectedEvent
    )
    const eventData = this.selectedEvent?.extendedProps?.data

    // Properly parse the date from the event and ensure it's a valid Date object
    let eventStart: Date
    if (this.selectedEvent.start instanceof Date) {
      eventStart = new Date(this.selectedEvent.start.getTime())
    } else {
      eventStart = new Date(this.selectedEvent.start)
    }

    let eventEnd: Date
    if (this.selectedEvent.end) {
      if (this.selectedEvent.end instanceof Date) {
        eventEnd = new Date(this.selectedEvent.end.getTime())
      } else {
        eventEnd = new Date(this.selectedEvent.end)
      }
    } else {
      eventEnd = new Date(eventStart.getTime() + 30 * 60000)
    }

    console.log('Parsed eventStart:', eventStart)
    console.log('Is valid date:', !isNaN(eventStart.getTime()))
    console.log('Original start string:', this.selectedEvent.start)

    const duration = DateTools.calcTimeDifference(eventStart, eventEnd)
    const technicianEmails =
      eventData?.usersTechnicians?.map((tech: any) => tech.email) || []
    const technicianIds = this.findTechnicianIdsByEmails(technicianEmails)

    // Gérer les créneaux horaires au format "HH:MM:SS"
    let startTimeSlot = null
    let endTimeSlot = null

    // Si l'événement a déjà des valeurs de créneau horaire
    if (eventData?.isTimeSlot) {
      // Si les créneaux sont déjà au format "HH:MM:SS", on les utilise directement
      if (
        typeof eventData.startTimeSlot === 'string' &&
        eventData.startTimeSlot.includes(':')
      ) {
        startTimeSlot = eventData.startTimeSlot
      }
      // Sinon, on les convertit en objets Date pour l'UI
      else if (eventData.startTimeSlot) {
        startTimeSlot = this.createTimeDate(eventData.startTimeSlot)
      }

      if (
        typeof eventData.endTimeSlot === 'string' &&
        eventData.endTimeSlot.includes(':')
      ) {
        endTimeSlot = eventData.endTimeSlot
      } else if (eventData.endTimeSlot) {
        endTimeSlot = this.createTimeDate(eventData.endTimeSlot)
      }

      // Déterminer si les valeurs correspondent à un créneau prédéfini
      this.determineTimeSlotType(startTimeSlot, endTimeSlot)
    }

    // Patch the form with a slight delay to ensure PrimeNG components are ready
    setTimeout(() => {
      this.shiftForm.patchValue({
        techniciansIds: technicianIds,
        startDate: eventStart,
        duration: duration,
        isTimeSlot: eventData?.isTimeSlot === 1,
        startTimeSlot: startTimeSlot,
        endTimeSlot: endTimeSlot,
      })

      console.log('Form patched with startDate:', eventStart)
      console.log(
        'Form value after patch:',
        this.shiftForm.get('startDate')?.value
      )

      // Force change detection for PrimeNG v18
      this.shiftForm.get('startDate')?.updateValueAndValidity()
    }, 0)

    // Après avoir initialisé le formulaire, vérifier la cohérence des créneaux
    if (
      this.shiftForm.get('isTimeSlot')?.value &&
      this.selectedTimeSlot !== 'custom'
    ) {
      this.checkTimeSlotConsistency()
    }
  }

  // Méthode utilitaire pour créer un objet Date à partir d'une chaîne "HH:MM:SS"
  private createTimeDate(timeString: string): Date {
    if (!timeString) return new Date(0)

    const date = new Date()
    const parts = timeString.split(':')

    if (parts.length >= 2) {
      date.setHours(parseInt(parts[0], 10))
      date.setMinutes(parseInt(parts[1], 10))
      date.setSeconds(parts.length > 2 ? parseInt(parts[2], 10) : 0)
    }

    return date
  }

  private findTechnicianIdsByEmails(emails: string[]): number[] {
    if (!emails || !emails.length) return []

    const techniciansList = this.allTechnicians.length
      ? this.allTechnicians
      : this.availableTechnicians

    return techniciansList
      .filter((tech) => emails.includes(tech.email))
      .map((tech) => tech.id)
  }

  hide(): void {
    this.visible = false
    this.visibleChange.emit(false)
    this.onCancel.emit()
    // Réinitialiser le flag pour permettre la prochaine initialisation
    this.formInitialized = false
  }

  submit(): void {
    if (this.shiftForm.valid && !this.submitting) {
      // Assurons-nous de récupérer les valeurs actuelles en utilisant getRawValue()
      const formData = this.shiftForm.getRawValue()
      formData.erpSource = this.selectedEvent?.extendedProps?.data?.erpSource

      // Formatter la date de début
      if (formData.startDate instanceof Date) {
        const startDate = new Date(formData.startDate)
        formData.startDateFormatted = {
          year: startDate.getFullYear(),
          month: startDate.getMonth() + 1,
          day: startDate.getDate(),
          hour: startDate.getHours(),
          minute: startDate.getMinutes(),
          second: startDate.getSeconds(),
        }
        formData.startDateOriginal = startDate
      }

      // Formatter la durée
      if (formData.duration instanceof Date) {
        const hours = formData.duration.getHours().toString().padStart(2, '0')
        const minutes = formData.duration
          .getMinutes()
          .toString()
          .padStart(2, '0')
        const seconds = formData.duration
          .getSeconds()
          .toString()
          .padStart(2, '0')
        formData.durationString = `${hours}:${minutes}:${seconds}`
      }

      // Formatter les créneaux horaires au format "HH:MM:SS"
      if (formData.isTimeSlot) {
        // Formatter startTimeSlot
        if (formData.startTimeSlot instanceof Date) {
          formData.startTimeSlot = DateTools.formatTimeOnly(
            formData.startTimeSlot
          )
        } else if (
          typeof formData.startTimeSlot === 'string' &&
          !formData.startTimeSlot.includes(':')
        ) {
          // Convertir si c'est une chaîne qui n'est pas déjà au format "HH:MM:SS"
          formData.startTimeSlot = this.formatTimeString(formData.startTimeSlot)
        }

        // Formatter endTimeSlot
        if (formData.endTimeSlot instanceof Date) {
          formData.endTimeSlot = DateTools.formatTimeOnly(formData.endTimeSlot)
        } else if (
          typeof formData.endTimeSlot === 'string' &&
          !formData.endTimeSlot.includes(':')
        ) {
          // Convertir si c'est une chaîne qui n'est pas déjà au format "HH:MM:SS"
          formData.endTimeSlot = this.formatTimeString(formData.endTimeSlot)
        }
      }

      // Émettre les données du formulaire sans modifier l'état visuel
      this.onSubmit.emit(formData)
    }
  }

  // Méthode utilitaire pour formater une chaîne de temps en "HH:MM:SS"
  private formatTimeString(timeStr: string): string {
    if (!timeStr) return '00:00:00'

    // Gérer les différents formats possibles
    const timeParts = timeStr.split(':')
    const hours = (timeParts[0] || '0').padStart(2, '0')
    const minutes = (timeParts[1] || '0').padStart(2, '0')
    const seconds = (timeParts[2] || '0').padStart(2, '0')

    return `${hours}:${minutes}:${seconds}`
  }

  toggleTimeSlot(): void {
    const isTimeSlot = this.shiftForm.get('isTimeSlot')?.value

    if (isTimeSlot) {
      this.shiftForm.get('startTimeSlot')?.setValidators([Validators.required])
      this.shiftForm.get('endTimeSlot')?.setValidators([Validators.required])

      // Définir le créneau matin par défaut quand on active l'option
      this.selectTimeSlot('morning')
    } else {
      this.shiftForm.get('startTimeSlot')?.clearValidators()
      this.shiftForm.get('endTimeSlot')?.clearValidators()
      this.selectedTimeSlot = 'custom'
    }

    this.shiftForm.get('startTimeSlot')?.updateValueAndValidity()
    this.shiftForm.get('endTimeSlot')?.updateValueAndValidity()
  }

  /**
   * Sélectionne un créneau horaire prédéfini
   */
  selectTimeSlot(slot: 'morning' | 'afternoon' | 'custom'): void {
    this.selectedTimeSlot = slot
    this.timeSlotMismatch = false

    const startTimeSlot = new Date()
    const endTimeSlot = new Date()

    // Réinitialiser les heures, minutes et secondes
    startTimeSlot.setHours(0, 0, 0, 0)
    endTimeSlot.setHours(0, 0, 0, 0)

    // Définir les heures en fonction du créneau sélectionné
    if (slot === 'morning') {
      // Matin: 8h-12h
      startTimeSlot.setHours(8, 0, 0)
      endTimeSlot.setHours(12, 0, 0)

      this.shiftForm.patchValue({
        startTimeSlot: startTimeSlot,
        endTimeSlot: endTimeSlot,
      })
    } else if (slot === 'afternoon') {
      // Après-midi: 13h-18h
      startTimeSlot.setHours(13, 0, 0)
      endTimeSlot.setHours(18, 0, 0)

      this.shiftForm.patchValue({
        startTimeSlot: startTimeSlot,
        endTimeSlot: endTimeSlot,
      })
    }
    // Si 'custom', ne rien faire pour permettre la saisie manuelle

    // Après avoir sélectionné un nouveau créneau, vérifier et ajuster la date si nécessaire
    if (slot !== 'custom') {
      const startDate = this.shiftForm.get('startDate')?.value
      if (startDate) {
        this.checkTimeSlotConsistency()
      }
    }
  }

  /**
   * Détermine si les heures correspondent à un créneau prédéfini
   */
  private determineTimeSlotType(startDate: any, endDate: any): void {
    if (!startDate || !endDate) {
      this.selectedTimeSlot = 'custom'
      return
    }

    let startHour: number, endHour: number

    if (startDate instanceof Date && endDate instanceof Date) {
      startHour = startDate.getHours()
      endHour = endDate.getHours()
    } else if (typeof startDate === 'string' && typeof endDate === 'string') {
      startHour = parseInt(startDate.split(':')[0], 10)
      endHour = parseInt(endDate.split(':')[0], 10)
    } else {
      this.selectedTimeSlot = 'custom'
      return
    }

    // Vérifier si correspond au créneau du matin (8h-12h)
    if (startHour === 8 && endHour === 12) {
      this.selectedTimeSlot = 'morning'
    }
    // Vérifier si correspond au créneau de l'après-midi (13h-18h)
    else if (startHour === 13 && endHour === 18) {
      this.selectedTimeSlot = 'afternoon'
    } else {
      this.selectedTimeSlot = 'custom'
    }
  }

  /**
   * Vérifie si le créneau horaire sélectionné est cohérent avec les valeurs actuelles des créneaux
   */
  private checkTimeSlotConsistency(): void {
    const startDate = this.shiftForm.get('startDate')?.value
    const startTimeSlot = this.shiftForm.get('startTimeSlot')?.value
    const endTimeSlot = this.shiftForm.get('endTimeSlot')?.value

    if (!startDate || !startTimeSlot || !endTimeSlot) {
      this.timeSlotMismatch = false
      return
    }

    // Extraire les heures
    let startDateHour: number = 0
    let startSlotHour: number = 0
    let endSlotHour: number = 0

    if (startDate instanceof Date) {
      startDateHour = startDate.getHours()
    }

    if (startTimeSlot instanceof Date) {
      startSlotHour = startTimeSlot.getHours()
    } else if (typeof startTimeSlot === 'string') {
      startSlotHour = parseInt(startTimeSlot.split(':')[0], 10)
    }

    if (endTimeSlot instanceof Date) {
      endSlotHour = endTimeSlot.getHours()
    } else if (typeof endTimeSlot === 'string') {
      endSlotHour = parseInt(endTimeSlot.split(':')[0], 10)
    }

    // Vérifier si l'heure de startDate est comprise dans le créneau
    if (startDateHour < startSlotHour || startDateHour >= endSlotHour) {
      this.timeSlotMismatch = true
    } else {
      this.timeSlotMismatch = false
    }

    // Si un créneau spécifique est sélectionné (matin/après-midi), vérifier également sa cohérence
    if (this.selectedTimeSlot !== 'custom') {
      const isValidMorning =
        this.selectedTimeSlot === 'morning' &&
        startSlotHour === 8 &&
        endSlotHour === 12

      const isValidAfternoon =
        this.selectedTimeSlot === 'afternoon' &&
        startSlotHour === 13 &&
        endSlotHour === 18

      if (!isValidMorning && !isValidAfternoon) {
        this.timeSlotMismatch = true
      }
    }
  }

  /**
   * Mise à jour du créneau pour correspondre à la date actuelle
   */
  updateTimeSlotToMatchDate(): void {
    const startDate = this.shiftForm.get('startDate')?.value
    if (!startDate || !(startDate instanceof Date)) {
      return
    }

    const currentHour = startDate.getHours()
    let newSlot: 'morning' | 'afternoon' | 'custom'

    // Déterminer le créneau approprié en fonction de l'heure de startDate
    if (currentHour >= 8 && currentHour < 12) {
      newSlot = 'morning'
    } else if (currentHour >= 13 && currentHour < 18) {
      newSlot = 'afternoon'
    } else {
      // Si l'heure ne correspond à aucun créneau, utiliser le créneau actuel
      // ou par défaut le matin si le créneau est 'custom'
      newSlot =
        this.selectedTimeSlot !== 'custom' ? this.selectedTimeSlot : 'morning'
    }

    // Appliquer le nouveau créneau
    this.selectedTimeSlot = newSlot

    const startTimeSlot = new Date()
    const endTimeSlot = new Date()

    // Configurer les heures du créneau
    if (newSlot === 'morning') {
      startTimeSlot.setHours(8, 0, 0)
      endTimeSlot.setHours(12, 0, 0)
    } else if (newSlot === 'afternoon') {
      startTimeSlot.setHours(13, 0, 0)
      endTimeSlot.setHours(18, 0, 0)
    }

    // Mettre à jour le formulaire
    this.shiftForm.patchValue({
      startTimeSlot: startTimeSlot,
      endTimeSlot: endTimeSlot,
    })

    this.timeSlotMismatch = false
  }
}
